/**
 * ¿Este navegador puede crear un contexto WebGL? MapLibre lo necesita; sin él
 * `new maplibregl.Map` tira y la página entera se cae.
 *
 * - Se prueba una sola vez por carga (cache en módulo).
 * - `reason` distingue política de empresa / GPU apagada / sin soporte, para el aviso.
 * - El reporte a `client_errors` va una vez por sesión (`reportWebGLUnavailableOnce`).
 * - En las rutas de acceso (login, registro, etc.) no se reporta: el mapa ahí es solo fondo.
 *
 * Ver docs/maps/map-webgl-unavailable.md.
 */
import { reportClientError } from '../../services/clientErrorService';
import { detectInAppBrowser } from '../../Utils/inAppBrowser';

/** @typedef {'enterprise_policy'|'gpu_disabled'|'unsupported'|'map_init_failed'} WebGLUnavailableReason */

/**
 * @typedef {{
 *   available: boolean,
 *   reason: WebGLUnavailableReason|null,
 *   statusMessage: string,
 * }} WebGLSupport
 */

const CONTEXT_NAMES = ['webgl2', 'webgl', 'experimental-webgl'];
const SESSION_REPORT_KEY = 'terrio_webgl_unavailable_reported';

/** @type {WebGLSupport|null} */
let cachedSupport = null;
let reportedInMemory = false;
/** Reporte que se salteó en una ruta de acceso; sale al llegar a otra ruta. */
let pendingReportContext = null;

/**
 * Rutas de `AuthFormRoutes` (App.js) dentro de `MapLayout`: comparten `AuthLayout`
 * y el mapa de `/app` queda de fondo. Sin WebGL el formulario anda igual, así que
 * ahí no se reporta (tráfico de anuncios en navegadores internos llenaba la consola).
 * `/auth/callback`, `/auth/logout` y `/meta-callback` no montan mapa: no hace falta listarlas.
 */
export const WEBGL_REPORT_SKIPPED_PATHS = Object.freeze([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/verify',
]);

/**
 * Ruta del HashRouter sin query ni barra final: `#/register?fbclid=x` → `/register`.
 *
 * @param {string} [hash]
 * @returns {string}
 */
export function getHashRoutePath(
  hash = typeof window !== 'undefined' ? window.location.hash : '',
) {
  let path = String(hash || '').replace(/^#/, '');
  path = path.split('?')[0].split('#')[0];
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return path.toLowerCase();
}

/**
 * @param {string} [pathname] ruta del router; si falta, se lee del hash actual
 * @returns {boolean}
 */
export function isWebGLReportSkippedRoute(pathname) {
  const path = pathname == null ? getHashRoutePath() : getHashRoutePath(pathname);
  return WEBGL_REPORT_SKIPPED_PATHS.includes(path);
}

/**
 * Chrome manda un `statusMessage` en `webglcontextcreationerror`. No es un contrato,
 * así que solo afinamos el texto del aviso; ante la duda, `unsupported`.
 *
 * @param {string} [message]
 * @returns {WebGLUnavailableReason}
 */
export function classifyWebGLFailure(message) {
  const text = String(message || '').toLowerCase();
  if (/polic/.test(text)) return 'enterprise_policy';
  if (/disabled|blocklist|blacklist|blocked|gpu access|hardware acceleration/.test(text)) {
    return 'gpu_disabled';
  }
  return 'unsupported';
}

/**
 * Prueba real (sin cache). `createCanvas` existe para los tests.
 *
 * @param {{ createCanvas?: () => HTMLCanvasElement|null }} [options]
 * @returns {WebGLSupport}
 */
export function detectWebGLSupport({ createCanvas } = {}) {
  let statusMessage = '';
  try {
    const canvas = createCanvas
      ? createCanvas()
      : (typeof document !== 'undefined' ? document.createElement('canvas') : null);
    if (!canvas || typeof canvas.getContext !== 'function') {
      return { available: false, reason: 'unsupported', statusMessage };
    }
    const onCreationError = (event) => {
      if (event && event.statusMessage) statusMessage = String(event.statusMessage);
    };
    if (typeof canvas.addEventListener === 'function') {
      canvas.addEventListener('webglcontextcreationerror', onCreationError, false);
    }
    let gl = null;
    for (let i = 0; i < CONTEXT_NAMES.length && !gl; i += 1) {
      try {
        gl = canvas.getContext(CONTEXT_NAMES[i]);
      } catch (_) {
        gl = null;
      }
    }
    if (typeof canvas.removeEventListener === 'function') {
      canvas.removeEventListener('webglcontextcreationerror', onCreationError, false);
    }
    if (!gl) {
      return { available: false, reason: classifyWebGLFailure(statusMessage), statusMessage };
    }
    // Liberar el contexto de prueba: el navegador limita cuántos hay vivos.
    try {
      const lose = typeof gl.getExtension === 'function' ? gl.getExtension('WEBGL_lose_context') : null;
      if (lose && typeof lose.loseContext === 'function') lose.loseContext();
    } catch (_) { /* noop */ }
    return { available: true, reason: null, statusMessage };
  } catch (error) {
    return {
      available: false,
      reason: classifyWebGLFailure(statusMessage || (error && error.message)),
      statusMessage: statusMessage || String((error && error.message) || ''),
    };
  }
}

/** @returns {WebGLSupport} */
export function getWebGLSupport() {
  if (!cachedSupport) cachedSupport = detectWebGLSupport();
  return cachedSupport;
}

/** @returns {boolean} */
export function isWebGLAvailable() {
  return getWebGLSupport().available;
}

/**
 * El constructor de MapLibre falló aunque la prueba pasó (contexto perdido, límite
 * de contextos, driver): cachear como no disponible para no reintentar en cada mapa.
 *
 * @param {unknown} error
 * @returns {WebGLSupport}
 */
export function markWebGLUnavailable(error) {
  const message = String((error && error.message) || error || '');
  const reason = classifyWebGLFailure(message);
  cachedSupport = {
    available: false,
    reason: reason === 'unsupported' ? 'map_init_failed' : reason,
    statusMessage: message,
  };
  return cachedSupport;
}

function readSessionFlag() {
  try {
    return window.sessionStorage.getItem(SESSION_REPORT_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeSessionFlag() {
  try {
    window.sessionStorage.setItem(SESSION_REPORT_KEY, '1');
  } catch (_) { /* sessionStorage bloqueado (iframe de terceros): queda el flag en memoria */ }
}

function alreadyReported() {
  return reportedInMemory || readSessionFlag();
}

function sendReport({ support, surface, embedded, inApp }) {
  reportedInMemory = true;
  pendingReportContext = null;
  writeSessionFlag();
  const info = support || getWebGLSupport();
  void reportClientError({
    error: new Error('WebGL no disponible: el mapa no se pudo crear'),
    source: 'window_error',
    metadata: {
      kind: 'webgl_unavailable',
      reason: info.reason || 'unsupported',
      status_message: info.statusMessage ? String(info.statusMessage).slice(0, 300) : null,
      surface: surface || null,
      embedded: embedded === true,
      in_app: inApp === undefined ? detectInAppBrowser() : inApp,
    },
  });
}

/**
 * Una vez por sesión. `client_errors.source` tiene CHECK en la DB
 * ('error_boundary' | 'window_error' | 'unhandled_rejection'): va como `window_error`
 * con `metadata.kind = 'webgl_unavailable'`. Nunca `error_boundary` (no es un crash de render).
 *
 * En una ruta de acceso (`WEBGL_REPORT_SKIPPED_PATHS`) no se envía **ni se marca la sesión**:
 * queda pendiente y `flushPendingWebGLReport` lo manda si después navega a otra ruta.
 *
 * @param {{
 *   support?: WebGLSupport,
 *   surface?: string,
 *   embedded?: boolean,
 *   pathname?: string,
 *   inApp?: 'facebook'|'instagram'|null,
 * }} [context] `pathname` e `inApp` son para tests; por defecto, hash y UA actuales.
 * @returns {boolean} `true` si se envió ahora
 */
export function reportWebGLUnavailableOnce({ support, surface, embedded, pathname, inApp } = {}) {
  if (alreadyReported()) return false;
  const context = { support, surface, embedded, inApp };
  if (isWebGLReportSkippedRoute(pathname)) {
    pendingReportContext = context;
    return false;
  }
  sendReport(context);
  return true;
}

/**
 * Si se salteó un reporte en una ruta de acceso y ahora la ruta es otra (p. ej. `/app`
 * después de registrarse), lo envía una vez. Lo llama `MapLayout` en cada cambio de ruta.
 *
 * @param {{ pathname?: string }} [options]
 * @returns {boolean} `true` si se envió ahora
 */
export function flushPendingWebGLReport({ pathname } = {}) {
  if (!pendingReportContext) return false;
  if (alreadyReported()) {
    pendingReportContext = null;
    return false;
  }
  if (isWebGLReportSkippedRoute(pathname)) return false;
  sendReport(pendingReportContext);
  return true;
}

export function resetWebGLSupportForTests() {
  cachedSupport = null;
  reportedInMemory = false;
  pendingReportContext = null;
  try {
    window.sessionStorage.removeItem(SESSION_REPORT_KEY);
  } catch (_) { /* noop */ }
}
